import { forwardRef, PropsWithChildren } from 'react';

import type { InteractionMode } from '../components/interactions-overlay/types.js';
import type { RelativeAbsolutePosition } from '../types/pointer-events.js';

export interface EventHandlers {
  onModeChange?: (event: { mode: InteractionMode; keyboard: boolean }) => void;
  onHover?: (event: {
    triggeredBy: string;
    position: RelativeAbsolutePosition;
  }) => void;
  onLeave?: () => void;
  onPanChange?: (event: { triggeredBy: string; delta: number }) => void;
  onTogglePin?: (event: {
    triggeredBy: string;
    position: RelativeAbsolutePosition;
    pin: boolean;
  }) => void;
  onReset?: (event: {
    triggeredBy: string;
    mousePosition?: RelativeAbsolutePosition;
  }) => void;
  onSelectStart?: (event: {
    triggeredBy: string;
    position: RelativeAbsolutePosition;
  }) => void;
  onZoomChange?: (event: {
    triggeredBy: string;
    deltaStart: number;
    deltaEnd: number;
    behavior: string;
  }) => void;
  onSelectEnd?: (event: {
    triggeredBy?: string;
    start?: RelativeAbsolutePosition;
    end?: RelativeAbsolutePosition;
    cancelled?: boolean;
  }) => void;
  onSelectChange?: (event: {
    triggeredBy: string;
    start: RelativeAbsolutePosition;
    end: RelativeAbsolutePosition;
    mode: string;
  }) => void;
}

export const eventHandlers: EventHandlers = {};

export const mockInteractionsOverlay = forwardRef<
  HTMLDivElement,
  PropsWithChildren<EventHandlers & Record<string, unknown>>
>(
  (
    {
      children,
      onModeChange,
      onHover,
      onLeave,
      onPanChange,
      onTogglePin,
      onReset,
      onSelectStart,
      onZoomChange,
      onSelectEnd,
      onSelectChange,
      selectionArea: _selectionArea,
      isZoomDisabled: _isZoomDisabled,
      isZoomXDisabled: _isZoomXDisabled,
      isPanDisabled: _isPanDisabled,
      pinned: _pinned,
      ...rest
    },
    ref,
  ) => {
    eventHandlers.onModeChange = onModeChange;
    eventHandlers.onHover = onHover;
    eventHandlers.onLeave = onLeave;
    eventHandlers.onPanChange = onPanChange;
    eventHandlers.onTogglePin = onTogglePin;
    eventHandlers.onReset = onReset;
    eventHandlers.onSelectStart = onSelectStart;
    eventHandlers.onZoomChange = onZoomChange;
    eventHandlers.onSelectEnd = onSelectEnd;
    eventHandlers.onSelectChange = onSelectChange;
    return (
      <div ref={ref} data-testid="chart-overlay" {...rest}>
        {children}
      </div>
    );
  },
);

export const mockPosition = (x: number, y: number = 0) => ({
  relative: { x, y },
  absolute: { x, y },
});
